import React from 'react';
import {
  C,
  CARD_W, VOTE_CARD_H, VOTE_CARD_GAP, CIRCLE_R, PLUS_R, TRIG_W,
  BRANCH_VSEP, BRANCH_STEM, HASH_GAP, TRIG_H,
  normalizeImgField,
} from '../flowCanvas.constants';
import VoteCardNode from './VoteCardNode';
import TriggerNode  from './TriggerNode';

const BURST_LABELS = {
  default: 'Default burst',
  anim1:   'Burst 1',
  anim2:   'Burst 2',
  anim3:   'Burst 3',
  anim4:   'Burst 4',
  anim5:   'Burst 5',
  anim6:   'Burst 6',
};

// ─── Zapper objects node — stacked VoteCardNodes + branch wiring + hashtag trigger ─
//
// Props:
//   step              – the zapperObjects customisation step
//   stepX             – BFS-computed X centre for the card stack
//   stepCY            – vertical centre for this node
//   parentStepX       – X centre of the parent circle (vote_start_node)
//   hashStep          – the linked zap_hashtag trigger step (null = none)
//   hashCY            – computed Y centre for the hashtag trigger
//   hashNodeX         – BFS X centre for the hashtag trigger node
//   items             – the object vals array (live-resolved in FlowCanvas)
//   activeStepId      – currently selected step id
//   activeGraphicIndex – currently selected object index
//   assetFolder / campaignId – for asset URL resolution
//   onObjectClick(idx)     – fired when a VoteCardNode is clicked
//   onAddObjectClick()     – fired when the + circle on the "Object image" branch is clicked
//   onHashtagAddClick()    – fired when the + circle on the "Zap hashtag" branch is clicked
//   onHashtagNodeClick()   – fired when the TriggerNode itself is clicked
//   canEditHashtags    – whether hashtag editing is allowed
//   onCommitHashtags   – commit handler
//   onRemoveObject     – passed directly to each VoteCardNode for deletion
//   onDraftHeightChange – passed to TriggerNode for live reflow

export default function ZapperObjectsNode({
  step, stepX, stepCY,
  parentStepX,
  hashStep, hashCY, hashNodeX,
  items = [],
  activeStepId, activeGraphicIndex,
  assetFolder, campaignId,
  onObjectClick, onAddObjectClick, onHashtagAddClick, onHashtagNodeClick,
  canEditHashtags = false,
  onCommitHashtags,
  hashtagFocusNonce = 0,
  onRemoveObject,
  onDraftHeightChange,
}) {
  const count    = Math.max(items.length, 1);
  const totalH   = count * (VOTE_CARD_H + VOTE_CARD_GAP) - VOTE_CARD_GAP;

  const hasParent      = parentStepX !== null && parentStepX !== undefined;
  const circleRightX   = hasParent ? parentStepX + CIRCLE_R : stepX - CARD_W / 2 - 50 + CIRCLE_R;
  const stackTopY      = stepCY - totalH / 2;

  const imgBranchOriginY  = stepCY - BRANCH_VSEP;
  const hashBranchOriginY = stepCY + BRANCH_VSEP;

  const cardCentreX = stepX;
  const cardLeftX   = cardCentreX - CARD_W / 2;
  const branchMidX  = Math.max(circleRightX + BRANCH_STEM, (circleRightX + cardLeftX) / 2);

  const firstCardY = stackTopY + VOTE_CARD_H / 2;
  const lastCardY  = stackTopY + (Math.max(items.length, 1) - 1) * (VOTE_CARD_H + VOTE_CARD_GAP) + VOTE_CARD_H / 2;

  const plusX = (circleRightX + branchMidX) / 2;
  const plusY = imgBranchOriginY;

  const hashLeftX = hashNodeX !== null && hashNodeX !== undefined ? hashNodeX - TRIG_W / 2 : null;

  const imgComplete = items.length > 0 && items.every(v => normalizeImgField(v.filename).name);
  const imgColor    = imgComplete ? C.purple : items.length > 0 ? C.orange : C.textDim;
  const hashColor   = '#a78bfa';

  // Derive branch label from the trigger step's title so Object Spawner shows "Spawn hashtag"
  const hashBranchLabel = hashStep
    ? hashStep.stepTitle.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
    : 'Zap hashtag';

  const totalFreq = items.reduce((s, v) => s + (v?.appearFrequency ?? 100), 0) || 100;

  return (
    <React.Fragment key={step.stepId}>

      {/* ── "Object image" branch: parent circle → + circle → stacked cards ── */}
      {hasParent && (
        <g style={{ pointerEvents: 'none' }}>
          {/* Horizontal stem from circle right edge to branchMidX */}
          <line x1={circleRightX} y1={imgBranchOriginY} x2={branchMidX} y2={imgBranchOriginY}
            stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
          {/* Branch label raised above the + circle */}
          <text x={(circleRightX + branchMidX) / 2} y={imgBranchOriginY - PLUS_R - 8}
            textAnchor="middle" fontSize="9.5" fontWeight="600" fill={imgColor}
            style={{ userSelect: 'none' }}>Object image</text>
          {/* Vertical jog from stem down to firstCardY (when needed) */}
          {Math.abs(imgBranchOriginY - firstCardY) > 2 && (
            <line x1={branchMidX} y1={imgBranchOriginY} x2={branchMidX} y2={firstCardY}
              stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
          )}
          {/* Vertical spine spanning all cards */}
          {items.length > 1 && (
            <line x1={branchMidX} y1={firstCardY} x2={branchMidX} y2={lastCardY}
              stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.5" />
          )}
          {/* Horizontal spur + arrival dot for each card */}
          {(items.length > 0 ? items : [null]).map((_, i) => {
            const cy = stackTopY + i * (VOTE_CARD_H + VOTE_CARD_GAP) + VOTE_CARD_H / 2;
            return (
              <React.Fragment key={i}>
                <line x1={branchMidX} y1={cy} x2={cardLeftX} y2={cy}
                  stroke={imgColor} strokeWidth="1.5" strokeOpacity="0.7" strokeLinecap="square" />
                <circle cx={cardLeftX} cy={cy} r="3" fill={imgColor} fillOpacity="0.9" />
              </React.Fragment>
            );
          })}
        </g>
      )}

      {/* ── + add-object circle on "Object image" branch stem ── */}
      {hasParent && (
        <g onClick={onAddObjectClick} style={{ cursor: 'pointer' }}>
          <circle cx={plusX} cy={plusY} r={PLUS_R} fill={C.greenText} />
          <text x={plusX} y={plusY + 1}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="16" fontWeight="300" fill="#fff"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
        </g>
      )}

      {/* ── "Zap hashtag" branch: parent circle → + circle → hashtag trigger ── */}
      {hasParent && hashStep && hashLeftX !== null && hashCY !== null && (() => {
        const hashBranchMidX = Math.min(
          Math.max(circleRightX + BRANCH_STEM, (circleRightX + hashLeftX) / 2),
          branchMidX - 20,
        );
        const sameY     = Math.abs(hashBranchOriginY - hashCY) < 2;
        const hashPlusX = (circleRightX + hashBranchMidX) / 2;
        const hashPlusY = hashBranchOriginY;
        const d = sameY
          ? `M ${circleRightX} ${hashBranchOriginY} H ${hashLeftX}`
          : `M ${circleRightX} ${hashBranchOriginY} H ${hashBranchMidX} V ${hashCY} H ${hashLeftX}`;
        return (
          <>
            <g style={{ pointerEvents: 'none' }}>
              <path d={d} stroke={hashColor} strokeWidth="1.5" fill="none"
                strokeOpacity="0.7" strokeLinecap="square" />
              <circle cx={hashLeftX} cy={hashCY} r="3" fill={hashColor} fillOpacity="0.9" />
              <text x={hashPlusX} y={hashBranchOriginY - PLUS_R - 8}
                textAnchor="middle" fontSize="9.5" fontWeight="600" fill={hashColor}
                style={{ userSelect: 'none' }}>{hashBranchLabel}</text>
            </g>
            {/* + circle on hashtag branch */}
            <g onClick={onHashtagAddClick} style={{ cursor: 'pointer' }}>
              <circle cx={hashPlusX} cy={hashPlusY} r={PLUS_R} fill={C.greenText} />
              <text x={hashPlusX} y={hashPlusY + 1}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="16" fontWeight="300" fill="#fff"
                style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
            </g>
          </>
        );
      })()}

      {/* ── Individual VoteCardNode per object, stacked vertically ── */}
      {items.length > 0 ? items.map((item, i) => {
        const cardY     = stackTopY + i * (VOTE_CARD_H + VOTE_CARD_GAP) + VOTE_CARD_H / 2;
        const isSel     = activeStepId === step.stepId && activeGraphicIndex === i;
        const burstLabel = BURST_LABELS[item?.destroyAnimation ?? 'default'] ?? 'Default burst';
        return (
          <VoteCardNode
            key={i}
            graphicItem={item}
            graphicIndex={i}
            teamLabel="Object"
            selected={isSel}
            onClick={() => onObjectClick && onObjectClick(i)}
            overrideX={cardCentreX}
            overrideY={cardY}
            assetFolder={assetFolder}
            campaignId={campaignId}
            teamTotalFreq={totalFreq}
            onDelete={items.length > 1 && onRemoveObject
              ? () => onRemoveObject(step, i)
              : null}
            burstAnimLabel={burstLabel}
          />
        );
      }) : (
        /* Empty state — no objects configured yet */
        <g key="empty" onClick={onAddObjectClick} style={{ cursor: 'pointer' }}>
          <rect x={cardCentreX - CARD_W / 2 + 2} y={stepCY - VOTE_CARD_H / 2 + 4}
            width={CARD_W} height={VOTE_CARD_H} rx="8" fill="#000" fillOpacity="0.3" />
          <rect x={cardCentreX - CARD_W / 2} y={stepCY - VOTE_CARD_H / 2}
            width={CARD_W} height={VOTE_CARD_H} rx="8"
            fill={C.card} stroke={C.orange} strokeWidth="1.5" strokeDasharray="5 3" />
          <text x={cardCentreX} y={stepCY - 16} textAnchor="middle" fontSize="11" fontWeight="600" fill={C.orange}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>No objects yet</text>
          <text x={cardCentreX} y={stepCY + 4} textAnchor="middle" fontSize="9" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Press</text>
          <text x={cardCentreX} y={stepCY + 18} textAnchor="middle" fontSize="20" fontWeight="300" fill={C.greenText}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
          <text x={cardCentreX} y={stepCY + 36} textAnchor="middle" fontSize="9" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>to add an object</text>
        </g>
      )}

      {/* ── Hashtag TriggerNode rendered inline so its Y tracks the live stack height ── */}
      {hashStep && hashCY !== null && (
        <TriggerNode
          key={hashStep.stepId}
          step={hashStep}
          selected={activeStepId === hashStep.stepId}
          onClick={onHashtagNodeClick}
          centreY={stepCY}
          overrideX={hashNodeX}
          overrideY={hashCY}
          editable={canEditHashtags}
          onCommitHashtags={onCommitHashtags}
          focusNonce={hashtagFocusNonce}
          onDraftHeightChange={onDraftHeightChange}
        />
      )}

    </React.Fragment>
  );
}
